import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { getJobs } from "@/lib/api";
import type { JobWithRelations, PaginatedJobs } from "@/types/db";

const PAGE_SIZE = 20;

interface UseInfiniteJobsOptions {
  initialJobs: JobWithRelations[];
  initialTotal: number;
  tags?: string[];
  search?: string;
  country?: string;
  state?: string;
}

export function useInfiniteJobs({
  initialJobs,
  initialTotal,
  tags = [],
  search,
  country,
  state,
}: UseInfiniteJobsOptions) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: ["jobs", { tags, search, country, state }],
      queryFn: async ({ pageParam }) => {
        return (await getJobs({
          data: {
            limit: PAGE_SIZE,
            offset: pageParam,
            tags: tags.length > 0 ? tags : undefined,
            search: search || undefined,
            country: country || undefined,
            state: state || undefined,
          },
        })) as PaginatedJobs;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.jobs.length < PAGE_SIZE) return undefined;
        return allPages.reduce((total, page) => total + page.jobs.length, 0);
      },
      initialData: {
        pages: [{ jobs: initialJobs, total: initialTotal }],
        pageParams: [0],
      },
      placeholderData: keepPreviousData,
    });

  const jobs = data?.pages.flatMap((page) => page.jobs) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  return {
    jobs,
    total,
    fetchNextPage,
    hasMore: hasNextPage,
    isFetchingNextPage,
    isLoading,
  };
}
